/* eslint-disable no-underscore-dangle */
import type {ImageProps, ObjectEvents, SerializedImageProps, TClassProperties, TOptions} from '@postermywall/fabricjs-2';
import {classRegistry, FabricImage} from '@postermywall/fabricjs-2';

export const fabricQRCodeImageDefaultValues: Partial<TClassProperties<FabricQRCodeImage>> = {
  qrBorderColor: '#000000',
  qrBorderThickness: 0,
  qrBorderRadius: 0,
};

interface UniqueFabricQRCodeImageProps {
  /**
   * Stroke color of the container border drawn around the image.
   */
  qrBorderColor: string;
  /**
   * Border thickness in canvas px, applied uniformly regardless of item scale
   * (same semantics as fabric's strokeUniform). 0 disables the border.
   */
  qrBorderThickness: number;
  /**
   * Corner radius of the border in element px, matching the corner radius
   * qr-code-styling bakes into the generated background.
   */
  qrBorderRadius: number;
}

export interface FabricQRCodeImageProps extends ImageProps, UniqueFabricQRCodeImageProps {}

/**
 * FabricImage that draws the QR container border itself in _render: unlike the stock fabric
 * rect stroke, this border follows the container's corner radius and stays crisp at any zoom
 * because it is drawn at render time instead of being baked into the generated QR raster.
 * PDF/SVG output rasterizes through fabric rendering (the QR item is not PDF-safe), so the
 * border needs no vector export path.
 */
export class FabricQRCodeImage<
    Props extends TOptions<FabricQRCodeImageProps> = Partial<FabricQRCodeImageProps>,
    SProps extends SerializedImageProps = SerializedImageProps,
    EventSpec extends ObjectEvents = ObjectEvents,
  >
  extends FabricImage<Props, SProps, EventSpec>
  implements UniqueFabricQRCodeImageProps
{
  declare qrBorderColor: string;
  declare qrBorderThickness: number;
  declare qrBorderRadius: number;

  static override type = 'FabricQRCodeImage';

  static override ownDefaults = fabricQRCodeImageDefaultValues;

  static override cacheProperties = [...FabricImage.cacheProperties, 'qrBorderColor', 'qrBorderThickness', 'qrBorderRadius'];

  static override getDefaults(): Record<string, unknown> {
    return {
      ...super.getDefaults(),
      ...FabricQRCodeImage.ownDefaults,
    };
  }

  constructor(imageElement: HTMLImageElement, options: Props) {
    super(imageElement, options);
    Object.assign(this, FabricQRCodeImage.ownDefaults);
    this.setOptions(options);
  }

  override _render(ctx: CanvasRenderingContext2D): void {
    super._render(ctx);
    this.#renderContainerBorder(ctx);
  }

  #renderContainerBorder(ctx: CanvasRenderingContext2D): void {
    if (this.qrBorderThickness <= 0) {
      return;
    }

    const scaleX = this.scaleX || 1;
    const scaleY = this.scaleY || 1;

    // Uniform-stroke trick borrowed from fabric's strokeUniform: draw the path in
    // scaled-out coordinates so lineWidth is unaffected by the item's scale.
    ctx.save();
    ctx.scale(1 / scaleX, 1 / scaleY);

    const width = this.width * scaleX;
    const height = this.height * scaleY;
    const inset = this.qrBorderThickness / 2;
    const left = -width / 2 + inset;
    const top = -height / 2 + inset;
    const right = width / 2 - inset;
    const bottom = height / 2 - inset;
    const cornerRx = Math.max(Math.min(this.qrBorderRadius * scaleX, (right - left) / 2) - inset, 0);
    const cornerRy = Math.max(Math.min(this.qrBorderRadius * scaleY, (bottom - top) / 2) - inset, 0);

    ctx.beginPath();
    ctx.moveTo(left + cornerRx, top);
    ctx.lineTo(right - cornerRx, top);
    ctx.ellipse(right - cornerRx, top + cornerRy, cornerRx, cornerRy, 0, -Math.PI / 2, 0);
    ctx.lineTo(right, bottom - cornerRy);
    ctx.ellipse(right - cornerRx, bottom - cornerRy, cornerRx, cornerRy, 0, 0, Math.PI / 2);
    ctx.lineTo(left + cornerRx, bottom);
    ctx.ellipse(left + cornerRx, bottom - cornerRy, cornerRx, cornerRy, 0, Math.PI / 2, Math.PI);
    ctx.lineTo(left, top + cornerRy);
    ctx.ellipse(left + cornerRx, top + cornerRy, cornerRx, cornerRy, 0, Math.PI, 1.5 * Math.PI);
    ctx.closePath();

    ctx.lineWidth = this.qrBorderThickness;
    ctx.strokeStyle = this.qrBorderColor;
    ctx.stroke();
    ctx.restore();
  }
}

classRegistry.setClass(FabricQRCodeImage);
